import { FC, useCallback, useEffect } from 'react';
import { Modal } from '../common/components';
import { Entity } from '../common/queryClient';
import { ObjectLockId } from './query/useLockQuery';
import { useApplyEditLock } from './useLocks';
import { ObjectType } from './types';
import { LockData } from './LockData';
import { useIdleTimer, IIdleTimer } from 'react-idle-timer';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './EditLock.translations.json';
import styled from 'styled-components';

const { addTranslations, useTranslation } = i18nNsFactory('locked-by');
addTranslations(translations);

export interface LockedByProps {
  entity: Pick<Entity, 'modifiedAt' | 'modifiedBy'> & { id?: number | null };
  id?: number;
  objectType: ObjectType;
  onTimeout: () => void;
  discriminator?: string;
  additionalLocks?: ObjectLockId[];
  checkLocks?: ObjectLockId[];
}

const MILLIS_PER_MINUTE = 60 * 1000;
const IDLE_TIMEOUT = 17 * MILLIS_PER_MINUTE;
const PROMPT_BEFORE_IDLE = 2 * MILLIS_PER_MINUTE;

export const EditLock: FC<LockedByProps> = ({
  entity,
  id,
  objectType,
  onTimeout,
  discriminator,
  additionalLocks,
  checkLocks,
}) => {
  const { t } = useTranslation();
  const { error, lockData } = useApplyEditLock(
    objectType,
    id ?? entity.id ?? undefined,
    discriminator,
    additionalLocks,
    checkLocks,
  );

  useEffect(() => {
    if (error) {
      Modal.error({
        title: t('modal.error.title'),
        content: t(error),
        onOk: () => {
          Modal.destroyAll();
          onTimeout();
        },
      });
    }
  }, [error, onTimeout, t]);

  const onIdle = useCallback(() => {
    Modal.destroyAll();
    onTimeout();
  }, [onTimeout]);

  const onPrompt = useCallback(
    (_?: Event, idleTimer?: IIdleTimer) => {
      Modal.confirm({
        title: t('title'),
        okText: t('button.continue'),
        cancelText: t('button.release'),
        content: <ContentWrapper>{t('label.object-locked')}</ContentWrapper>,
        onCancel() {
          onTimeout();
        },
        onOk() {
          idleTimer?.reset();
        },
        width: '30em',
      });
    },
    [onTimeout, t],
  );

  useIdleTimer({
    onIdle,
    onPrompt,
    promptBeforeIdle: PROMPT_BEFORE_IDLE,
    timeout: IDLE_TIMEOUT,
    throttle: 500,
  });

  return <LockData entity={entity} error={error} lockData={lockData} />;
};

export const ContentWrapper = styled.span`
  white-space: pre-wrap;
`;
